import React, { useState, useEffect, useMemo } from 'react';
import Layout from "../Component/Layout";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { ORDER_API, MASTER_API } from "../apiConfig";
import { authHeaders } from "../utils/authHeaders";
import { FileText } from 'lucide-react';

// --- Shared dropdown styling (matches Home / Product Master / Sales Order look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 36,
        height: 36,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '36px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

const USER_MANUAL_SCOPE = {
    moduleName: "Planning",
    process: "Planning",
    subProcess: "Order Tree"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function OrderTree() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // ── Filters (react-select, client-side against the already-loaded list) ──
    const [soFilter, setSoFilter] = useState(null);
    const [codeFilter, setCodeFilter] = useState(null);
    const [descFilter, setDescFilter] = useState(null);

    // ── BOM tree modal (Batch Tree-style, opened via the View button) ──
    const [showBomTreeModal, setShowBomTreeModal] = useState(false);
    const [bomTreeLoading, setBomTreeLoading] = useState(false);
    const [bomTree, setBomTree] = useState(null);
    const [expandedBomNodes, setExpandedBomNodes] = useState({});
    const [bomTreeProductCode, setBomTreeProductCode] = useState(null);

    // Unique option lists, built from whatever's currently loaded.
    const soOptions = useMemo(() => (
        [...new Set(rows.map(r => r.so_id).filter(Boolean))]
            .map(v => ({ value: v, label: v }))
    ), [rows]);

    const codeOptions = useMemo(() => (
        [...new Set(rows.map(r => r.product_code).filter(Boolean))]
            .map(v => ({ value: v, label: v }))
    ), [rows]);

    const descOptions = useMemo(() => (
        [...new Set(rows.map(r => r.description).filter(Boolean))]
            .map(v => ({ value: v, label: v }))
    ), [rows]);

    const filteredRows = useMemo(() => {
        return rows.filter(row => {
            if (soFilter && row.so_id !== soFilter.value) return false;
            if (codeFilter && row.product_code !== codeFilter.value) return false;
            if (descFilter && row.description !== descFilter.value) return false;
            return true;
        });
    }, [rows, soFilter, codeFilter, descFilter]);

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/order-tree/list`, {
                headers: authHeaders()
            });
            const data = await res.json();
            setRows(data.data || []);
        } catch (err) {
            console.error("fetchList Error:", err);
            toast.error("Failed to load Order Tree list");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    // ================= BOM TREE (View button — same endpoint shape as Batch Tree) =================
    const openBomTree = async (productCode, soId, itemNumber) => {
        setShowBomTreeModal(true);
        setBomTree(null);
        setExpandedBomNodes({});
        setBomTreeProductCode(productCode);
        setBomTreeLoading(true);
        try {
            const params = new URLSearchParams();
            if (soId != null) params.set("soId", soId);
            if (itemNumber != null) params.set("itemNumber", itemNumber);
            const qs = params.toString();
            const url = `${ORDER_API}/order-tree/${encodeURIComponent(productCode)}/bom-tree${qs ? `?${qs}` : ""}`;
            const res = await fetch(url, { headers: authHeaders() });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to load tree");
            setBomTree(data.data);
        } catch (err) {
            console.error("openBomTree Error:", err);
            toast.error("Failed to load BOM tree");
        } finally {
            setBomTreeLoading(false);
        }
    };

    const closeBomTree = () => {
        setShowBomTreeModal(false);
        setBomTree(null);
        setExpandedBomNodes({});
        setBomTreeProductCode(null);
    };

    const toggleBomNode = (key) => {
        setExpandedBomNodes(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const renderBomNode = (node, level = 0) => {
        if (!node) return null;

        const hasChildren = node.children && node.children.length > 0;
        const nodeKey = `${node.code}-${node.batch || 'nb'}`;
        const expanded = expandedBomNodes[nodeKey] ?? true;

        return (
            <div key={nodeKey} style={{ marginLeft: level * 25, marginTop: 6 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "8px 12px",
                        background: level === 0 ? "#dbeafe" : "#ffffff",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        boxShadow: "0 2px 5px rgba(0,0,0,0.08)"
                    }}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={() => toggleBomNode(nodeKey)}
                            style={{
                                width: 20,
                                height: 20,
                                fontSize: 12,
                                border: "none",
                                borderRadius: "50%",
                                background: "#2563eb",
                                color: "white",
                                cursor: "pointer"
                            }}
                        >
                            {expanded ? "-" : "+"}
                        </button>
                    ) : (
                        <span style={{ fontSize: 20 }}>&bull;</span>
                    )}

                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
                            {node.code}
                            {node.pegging && node.pegging.length > 0 && (
                                <span style={{ fontWeight: 500, fontSize: 11, color: "#2563eb", marginLeft: 6 }}>
                                    (
                                    {node.pegging
                                        .filter((peg) => peg.child_element_description)
                                        .map((peg) => {
                                            const qty =
                                                peg.child_qty !== undefined && peg.child_qty !== null && peg.child_qty !== ""
                                                    ? Number(peg.child_qty).toFixed(2)
                                                    : "";
                                            return qty ? `${peg.child_element_description}: ${qty}` : peg.child_element_description;
                                        })
                                        .filter((v, i, arr) => arr.indexOf(v) === i)
                                        .join(", ")}
                                    )
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 10, color: "#666" }}>
                            {node.description || "No Description"}
                        </div>
                        <div style={{ display: "flex", gap: 14, marginTop: 2 }}>
                            {node.quantity !== undefined && node.quantity !== null && (
                                <div style={{ fontSize: 10 }}>
                                    Qty: <strong style={{ color: "#334155" }}>{node.quantity}</strong>
                                </div>
                            )}
                            {node.uom && (
                                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                                    UOM: <strong style={{ color: "#334155" }}>{node.uom}</strong>
                                </div>
                            )}
                            {node.base_unit && (
                                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                                    Base Unit: <strong style={{ color: "#334155" }}>{node.base_unit}</strong>
                                </div>
                            )}
                        </div>
                    </div>

                    {hasChildren && (
                        <span
                            style={{
                                background: "#e0f2fe",
                                padding: "2px 8px",
                                borderRadius: 20,
                                fontSize: 10
                            }}
                        >
                            {node.children.length} items
                        </span>
                    )}
                    {!hasChildren && (
                        <span
                            style={{
                                background: "#fef3c7",
                                color: "#92400e",
                                padding: "2px 8px",
                                borderRadius: 20,
                                fontSize: 10
                            }}
                        >
                            Raw Material
                        </span>
                    )}
                </div>

                {expanded &&
                    hasChildren &&
                    node.children.map((child) => renderBomNode(child, level + 1))}
            </div>
        );
    };

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Order Tree</div>
                    <div style={styles.pageSubtitle}>View Sales Order line items</div>
                </div>

                <button
                    onClick={() => {
                        setManualExists(null);
                        setManualFile(null);
                        setShowManual(true);
                        fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                            .then((res) => res.json())
                            .then((data) => {
                                const norm = (v) => String(v || "").trim().toLowerCase();
                                const match = (data?.data || []).find((m) =>
                                    norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                    norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                    norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                );
                                if (match && match.drive_link) {
                                    setManualFile(match);
                                    setManualExists(true);
                                } else {
                                    setManualExists(false);
                                }
                            })
                            .catch(() => setManualExists(false));
                    }}
                    title="View User Manual"
                    style={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        color: "#3b82f6",
                        fontSize: "14px",
                        fontWeight: "600",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                >
                    <FileText size={18} />
                    User Manual
                </button>
            </div>

            {/* --- User Manual Popup Modal --- */}
            {showManual && (
                <div
                    onClick={() => setShowManual(false)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(15, 23, 42, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
                        {/* Modal Header */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "14px 20px",
                                borderBottom: "1px solid #e2e8f0",
                            }}
                        >
                            <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                User Manual
                            </h4>
                            <button
                                onClick={() => setShowManual(false)}
                                style={{
                                    border: "none",
                                    background: "#f1f5f9",
                                    borderRadius: "8px",
                                    width: "32px",
                                    height: "32px",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#475569",
                                    lineHeight: 1,
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                        {/* PDF Viewer / Fallback */}
                        {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25;
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}


            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>SO No</label>
                        <Select
                            classNamePrefix="react-select"
                            options={soOptions}
                            value={soFilter}
                            onChange={setSoFilter}
                            placeholder="Search SO No"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Product Code</label>
                        <Select
                            classNamePrefix="react-select"
                            options={codeOptions}
                            value={codeFilter}
                            onChange={setCodeFilter}
                            placeholder="Search Product Code"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Description</label>
                        <Select
                            classNamePrefix="react-select"
                            options={descOptions}
                            value={descFilter}
                            onChange={setDescFilter}
                            placeholder="Search Description"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>SO No</th>
                            <th style={styles.erpTh}>Item No</th>
                            <th style={styles.erpTh}>Product Code</th>
                            <th style={styles.erpTh}>Description</th>
                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>View</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td style={styles.erpTd} colSpan={6}>Loading...</td></tr>
                        ) : filteredRows.length === 0 ? (
                            <tr><td style={styles.erpTd} colSpan={6}>No records found</td></tr>
                        ) : (
                            filteredRows.map((row) => (
                                <tr key={row.id}>
                                    <td style={styles.erpTd}>{row.so_id}</td>
                                    <td style={styles.erpTd}>{row.item_no}</td>
                                    <td style={styles.erpTd}>{row.product_code}</td>
                                    <td style={styles.erpTd}>{row.description}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{row.quantity}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                        <button
                                            type="button"
                                            style={styles.viewBtn}
                                            onClick={() => openBomTree(row.product_code, row.so_id, row.id)}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ================= BOM TREE MODAL ================= */}
            {showBomTreeModal && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.5)",
                        zIndex: 1300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={closeBomTree}
                >
                    <div
                        style={{
                            width: "70vw",
                            height: "75vh",
                            padding: 14,
                            background: "#f8fafc",
                            borderRadius: 12,
                            overflowY: "auto"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 14
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <span
                                    style={{
                                        background: "#dbeafe",
                                        color: "#1d4ed8",
                                        padding: "3px 10px",
                                        borderRadius: 20,
                                        fontSize: 12,
                                        fontWeight: 700
                                    }}
                                >
                                    {bomTreeProductCode}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={closeBomTree}
                                style={{
                                    background: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 14px",
                                    borderRadius: 6,
                                    cursor: "pointer",
                                    fontSize: 12,
                                    fontWeight: 600
                                }}
                            >
                                Close
                            </button>
                        </div>

                        {bomTreeLoading ? (
                            <div style={{ fontSize: 12, color: '#64748b' }}>Loading...</div>
                        ) : bomTree ? (
                            renderBomNode(bomTree)
                        ) : (
                            <div style={{ fontSize: 12, color: '#64748b' }}>No tree data.</div>
                        )}
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={2500} />
        </Layout>
    );
}

const styles = {
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155', display: 'block' },
    viewBtn: {
        background: '#2563eb',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        padding: '5px 12px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
    },
};

export default OrderTree;